import { useQuery } from '@tanstack/react-query';
import { bairrosApi, type BairroApi } from '@/lib/api/bairros';
import { useAuthStore } from '@/lib/stores/authStore';
import type { BairroData } from '@/lib/stores/mockStore';

/**
 * Território no formato que as telas já consomem, acrescido dos números vivos
 * do gabinete e do id real para escrita.
 *
 * As 23 telas que usam território foram escritas sobre os nomes curtos do mock
 * (`n`, `pop`, `eleit`...). Traduzir aqui, num ponto só, evita reescrever ~11 mil
 * linhas de tela e mantém `id` como o slug, que é a chave usada em todas as
 * referências cruzadas (`bairroActiveId`, filtros, mapas).
 */
export interface BairroView extends BairroData {
  /** ULID do registro — necessário para editar ou excluir. */
  bairroId: string;
  /**
   * UF e município do território, como estão no banco. São a fonte única dos
   * selects encadeados de abrangência (motor de convite da Agenda); sem eles a
   * tela teria de cravar uma lista própria de UFs, como fazia o protótipo.
   */
  uf: string;
  municipio: string;
  contatosCadastrados: number;
  liderancasAtivas: number;
  solicitacoesAbertas: number;
}

export function paraBairroView(b: BairroApi): BairroView {
  return {
    id: b.slug,
    bairroId: b.id,
    n: b.nome,
    uf: b.uf,
    municipio: b.municipio,
    pop: b.populacao,
    dom: b.domicilios,
    eleit: b.eleitores,
    renda: b.renda_media,
    cad: b.cobertura_cadastro,
    votos: b.votos,
    // `numeric` do Postgres chega como string no JSON
    pct: Number(b.percentual) || 0,
    ubs: b.ubs,
    ubsIdeal: b.ubs_ideal,
    fila: b.fila_dias,
    pav: b.pavimentacao_pct,
    escuros: b.pontos_escuros,
    creche: b.fila_creche,
    esgoto: b.esgoto_pct,
    pracas: b.pracas,
    nec: b.necessidade,
    fill: b.mapa_cor || '#FFD694',
    path: b.mapa_path || '',
    lx: b.mapa_label_x ?? 0,
    ly: b.mapa_label_y ?? 0,
    contatosCadastrados: b.contatos_cadastrados,
    liderancasAtivas: b.liderancas_ativas,
    solicitacoesAbertas: b.solicitacoes_abertas,
  };
}

export function useBairros() {
  const token = useAuthStore((s) => s.token) || '';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bairros'],
    queryFn: () => bairrosApi.list(token),
    enabled: !!token,
    // Território muda pouco; evita refetch a cada troca de tela.
    staleTime: 5 * 60_000,
  });

  const bairros: BairroView[] = (data ?? []).map(paraBairroView);

  return { bairros, isLoading, isError };
}
